/**
 * Вход через Telegram Login Widget — §6.1, §13.2.
 *
 * Виджет — чужой скрипт с telegram.org; он рисует кнопку и после согласия
 * человека вызывает наш обработчик с набором полей и подписью `hash`.
 * Дальше эти поля уходят на сервер **как есть**: проверять подпись в
 * браузере бессмысленно — тот, кто подделывает данные, подделает и
 * проверку. Единственная настоящая проверка — серверная (§6.1), и она уже
 * написана (`identity.verify_login_widget`).
 *
 * Домен, на котором виджет соглашается работать, задаётся у @BotFather
 * командой `/setdomain`. Без этого кнопка не появится — это ограничение
 * Telegram, а не наше.
 */

import { useEffect, useRef, useState } from 'react';
import { Loading } from './states';

export interface TelegramAuthResult {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

declare global {
  interface Window {
    onTelegramAuth?: (user: TelegramAuthResult) => void;
  }
}

export function TelegramLoginButton({
  botUsername,
  onAuth,
}: {
  botUsername: string;
  onAuth: (user: TelegramAuthResult) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !botUsername) return;

    window.onTelegramAuth = onAuth;

    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.setAttribute('data-telegram-login', botUsername);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-userpic', 'false');
    script.setAttribute('data-request-access', 'write');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    script.onerror = () => setFailed(true);
    container.appendChild(script);

    return () => {
      container.innerHTML = '';
      delete window.onTelegramAuth;
    };
  }, [botUsername, onAuth]);

  return (
    <div className="stack" style={{ alignItems: 'center' }}>
      <div ref={containerRef} />
      {failed && (
        <p className="faint">
          Кнопка Telegram не загрузилась — проверьте соединение с интернетом и то, что домен
          добавлен боту командой /setdomain.
        </p>
      )}
    </div>
  );
}

/** Экран входа целиком: заголовок, кнопка, объяснение. */
export function LoginScreen({
  botUsername,
  loginAvailable,
  onAuth,
  pending,
  failedText,
}: {
  botUsername: string;
  loginAvailable: boolean;
  onAuth: (user: TelegramAuthResult) => void;
  pending: boolean;
  failedText?: string;
}) {
  if (pending) {
    return <Loading text="Вхожу…" />;
  }

  return (
    <div className="state">
      <h1 className="state__title">Тоскана</h1>
      <p className="state__text">
        Вход только через Telegram: отдельных паролей в системе нет — ни у сотрудника, ни у
        владельца.
      </p>
      {loginAvailable ? (
        <TelegramLoginButton botUsername={botUsername} onAuth={onAuth} />
      ) : (
        <p className="state__text">
          Вход в веб на этом сервере ещё не настроен: администратору нужно заполнить
          WEB_LOGIN_BOT_TOKEN и WEB_LOGIN_BOT_USERNAME в файле .env.
        </p>
      )}
      {failedText && <p className="state__text">{failedText}</p>}
    </div>
  );
}
